import React, { useState } from 'react';
import { Box } from '@mantine/core';
import './StorageTimeline.css'; // <-- Import the CSS file
import { StorageLocation } from '../../../models/StorageLocation';
import { RentContract } from '../../../models/RentContract';
import { getNext12Months } from './getNext12Months';
import { FilterControls } from '../../rentContract/Customer/RentContractsForOrg';

type ContractFilter = 'all' | 'active' | 'future' | 'past';

interface Month {
    label: string;
    startDate: Date;
    endDate: Date;
}

interface ContractPosition {
    left: number; // Percentage from the left
    width: number; // Percentage width
}

interface StorageTimelineProps {
    storageLocations: StorageLocation[];
    rentContracts: RentContract[];
}

const StorageTimeline: React.FC<StorageTimelineProps> = ({
                                                             storageLocations,
                                                             rentContracts,
                                                         }) => {
    const [filter, setFilter] = useState<ContractFilter>('all');
    const [searchText, setSearchText] = useState('');

    const now = new Date();

    // 1) Filter rent contracts by status (all, active, future, past)
    const filteredContracts = rentContracts.filter((contract) => {
        const start = new Date(contract.startDate);
        const end = new Date(contract.endDate);
        const isActive = now >= start && now <= end;
        const isFuture = now < start;
        const isPast = now > end;

        if (filter === 'active') return isActive;
        if (filter === 'future') return isFuture;
        if (filter === 'past') return isPast;
        return true; // 'all'
    });

    // 2) Filter rent contracts by search text
    const lowerSearch = searchText.toLowerCase();
    const displayedContracts = filteredContracts.filter((c) => {
        const fields = [
            c.id.toString(),
            c.storageLocation.name,
            c.firstName,
            c.lastName,
            c.email,
            c.phoneNumber,
            c.startDate,
            c.endDate,
            c.type,
            c.description,
            c.monthlyPrice.toString(),
        ];
        return fields.join(' ').toLowerCase().includes(lowerSearch);
    });

    // 3) If nothing is displayed, show a "No results" message
    if (displayedContracts.length === 0) {
        return (
            <>
                <FilterControls
                    filter={filter}
                    setFilter={setFilter}
                    searchText={searchText}
                    setSearchText={setSearchText}
                />
                <p>No rent contracts match your search/filter criteria.</p>
            </>
        );
    }

    // Prepare data for the timeline
    const months: Month[] = getNext12Months();
    const timelineStart = new Date(months[0].startDate);
    const timelineEnd = new Date(months[months.length - 1].endDate);

    // Helper to calculate the horizontal bar position in percentages
    const calculateContractPosition = (
        contract: RentContract,
        timelineStart: Date,
        timelineEnd: Date
    ): ContractPosition | null => {
        const contractStart = new Date(contract.startDate);
        const contractEnd = new Date(contract.endDate);

        // Clip the contract to the displayed timeline
        const adjustedStart = contractStart < timelineStart ? timelineStart : contractStart;
        const adjustedEnd = contractEnd > timelineEnd ? timelineEnd : contractEnd;

        // If it is completely out of range, skip
        if (adjustedEnd < timelineStart || adjustedStart > timelineEnd) return null;

        const totalDuration = timelineEnd.getTime() - timelineStart.getTime();
        const contractStartOffset = adjustedStart.getTime() - timelineStart.getTime();
        const contractDuration = adjustedEnd.getTime() - adjustedStart.getTime();

        return {
            left: (contractStartOffset / totalDuration) * 100,
            width: (contractDuration / totalDuration) * 100,
        };
    };

    return (
        <>
            <FilterControls
                filter={filter}
                setFilter={setFilter}
                searchText={searchText}
                setSearchText={setSearchText}
            />


            {/* 4) The container that allows horizontal scrolling */}
            <Box className="timeline-scroll-container">
                <div className="timeline-grid">
                    {/* Header row with 1 sticky column + monthly columns */}
                    <div className="timeline-header">
                        <div className="timeline-header-cell storage-location-cell sticky-col">
                            Stellplatz
                        </div>
                        {months.map((month, index) => (
                            <div key={index} className="timeline-header-cell month-cell">
                                {month.label}
                            </div>
                        ))}
                    </div>

                    {/* Rows for each storage location */}
                    {storageLocations.map((location) => {
                        // Filter contracts for this location
                        const locationContracts = displayedContracts.filter(
                            (contract) => contract.storageLocation.id === location.id
                        );

                        return (
                            <div key={location.id} className="timeline-row">
                                {/* Left sticky column with the location name */}
                                <div className="storage-location-name sticky-col">
                                    {location.name}
                                </div>

                                {/* The timeline-cells area for the contract bars */}
                                <div className="timeline-cells">
                                    {locationContracts.map((contract) => {
                                        const position = calculateContractPosition(
                                            contract,
                                            timelineStart,
                                            timelineEnd
                                        );
                                        if (!position) return null;

                                        // Inline styles for the bar
                                        const barStyle: React.CSSProperties = {
                                            left: `${position.left}%`,
                                            width: `${position.width}%`,
                                            backgroundColor: '#40C057',
                                            height: '20px',
                                            borderRadius: '4px',
                                            position: 'absolute',
                                            top: '10px',
                                            opacity: 0.7,
                                            transition: 'opacity 0.3s',
                                            cursor: 'pointer',
                                            zIndex: 1,
                                        };

                                        return (
                                            <div
                                                key={contract.id}
                                                className="rent-contract-bar"
                                                style={barStyle}
                                                title={`${contract.firstName} ${contract.lastName}: ${contract.description}`}
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </Box>
        </>
    );
};

export default StorageTimeline;
